import { Badge, Button, CodeDisplay, Icon, GroupedRowLabelContext, Tooltip } from '@aidkitorg/component-library'
import { ZoomableImage } from '../../../Components/TurnableImage'

import { ColumnDef } from '@tanstack/react-table';
import { useLocalizedStrings } from '../../../Localization';
import { gatherLinkTos, toAdminPanelHref, type LinkTemplateGenerator } from '../../../utils/dashboardUtils';
import { isOrderableColumn, nameForColumn } from '@aidkitorg/types/lib/translation/expr_to_sql';
import { isAidKitDocumentsUrl } from '../../../utils/signedS3Urls';

export const SYNTHETIC_UID_COLUMN_NAME = "__synthetic_uid__";
export const SYNTHETIC_VIEW_APPLICANTS_COLUMN_NAME = "__synthetic_view_applicants__";
export const SYNTHETIC_LINKS_TO_SECTION_COLUMN_NAME = "__synthetic_links_to_section__";

/**
 * Renders the View Applicants cell with a link to the applicant page
 * @param uid - The applicant UID value from getValue()
 * @returns React element or '--' if no UID
 */
export const renderViewApplicantsCell = (uid: any) => {
  const L = useLocalizedStrings()

  if (!uid) {
    return '--';
  }

  return (
    <Tooltip text={L.admin_panel.reports.view_tooltip}>
      <a
        href={`/admin-panel/a/${uid}`}
        target="_blank"
        rel="noopener noreferrer"
        className="p-1 hover:bg-gray-100 rounded transition-colors inline-flex text-gray-700"
        aria-label={L.admin_panel.reports.view_tooltip}
      >
        <Icon name="EyeIcon" type="solid" size="md" role="decorative" />
      </a>
    </Tooltip>
  );
};

/**
 * Renders a linksToSection cell with a link to a specific subsurvey
 * @param uid - The applicant UID
 * @param subsurveyPath - The subsurvey path to link to
 * @param label - The label for the link
 * @returns React element or '--' if no UID
 */
export const renderLinksToSectionCell = (uid: any, subsurveyPath: string, label: string) => {
  if (!uid) {
    return '--';
  }

  return (
    <a
      href={`/admin-panel/a/${uid}/${subsurveyPath}`}
      target="_blank"
      rel="noopener noreferrer"
      className="p-1 hover:bg-gray-100 rounded transition-colors inline-flex text-gray-700"
      aria-label={label}
    >
      <Icon name="EyeIcon" type="solid" size="md" role="decorative" />
    </a>
  );
};

const getFilenameFromUrl = (url: string): string | null => {
  try {
    const urlWithoutParams = url.split('?')[0];
    const filename = urlWithoutParams.split('/').pop();
    if (!filename) return null;
    return filename.length > 30 ? `${filename.slice(0, 15)}...${filename.slice(-12)}` : filename;
  } catch {
    return null;
  }
};

/**
 * Renders a link cell with button(s) for URLs
 * @param urlsString - The URL(s) as a comma-separated string
 * @param displayMode - 'filename' shows the filename from URL, 'view' shows "View" text
 * @returns React element with stacked button links or '--' if no URLs
 */
export function renderGroupedRowLabel(
  context: GroupedRowLabelContext<any>,
  columns: ColumnDef<any, any>[],
  groupCounts?: Record<string, number>,
) {
  const col = columns.find(c => c.id === context.groupingColumnId);
  const colLabel = col && 'header' in col && typeof col.header === 'string' ? col.header : context.groupingColumnId;
  const value = context.groupingValue;
  const displayValue = value === null || value === undefined || value === '' ? 'N/A' : String(value);
  const groupKey = value === null || value === undefined || value === '' ? '' : String(value);
  const totalCount = groupCounts?.[groupKey] ?? context.groupCount;
  return (
    <span className="flex items-center gap-2">
      <span>{colLabel}: {displayValue}</span>
      <Badge variant="neutral">{totalCount}</Badge>
    </span>
  );
}

export const renderLinkCell = (urlsString: string, displayMode: 'filename' | 'view' = 'filename') => {
  if (!urlsString) {
    return '--';
  }

  const L = useLocalizedStrings()
  // Parse the URLs
  const urls = urlsString.split(',').map((url: string) => url.trim());

  return (
    <div className="flex flex-col gap-1 items-start">
      {urls.map((url: string, index: number) => (
        <Button
          key={index}
          href={url}
          variant="link"
          openInNewWindow
          className="!justify-start"
          iconPlacement="right"
          icon={<Icon size="sm" role="decorative" name="ArrowTopRightOnSquareIcon" />}
        >
          <div className="inline-flex gap-2 items-center text-left size-min">
            {displayMode === "view"
              ? L.admin_panel.reports.view_url
              : (getFilenameFromUrl(url) ?? `${L.admin_panel.reports.attachment}_${index + 1}`)}
          </div>
        </Button>
      ))}
    </div>
  );
};

export type ReportColumnData = {
  key: string;
  label: string;
  fieldType?: string;
  encrypted?: boolean;
}

export type LinksToSectionInfo = {
  subsurveyPath: string;
  label: string;
}

export const formatColumnLabel = (key: string): string => {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

export function resolveLinksToSectionInfo(linksToSection: any): LinksToSectionInfo | undefined {
  if (!linksToSection?.subsurvey) return undefined;
  const subsurveyPath = typeof linksToSection.subsurvey === 'string'
    ? linksToSection.subsurvey
    : linksToSection.subsurvey.path;
  const label = linksToSection.label
    ? (typeof linksToSection.label === 'string' ? linksToSection.label : linksToSection.label.en)
    : subsurveyPath;
  return { subsurveyPath, label };
}

export type GenerateReportColumnsParams = {
  reportData: { columns: ReportColumnData[] } | null;
  isCustomQuery?: boolean;
  configColumns?: readonly any[];
  encryptedFields?: readonly string[];
  groupByField?: string;
  linksToSectionInfo?: LinksToSectionInfo;
  linkGensByCol: Record<string, LinkTemplateGenerator[]>;
  signedS3Urls?: Record<string, string | undefined>;
}

/**
 * Builds ColumnDef[] for a report table. Handles both custom-query reports
 * (columns derived from query result) and config-based reports (ordered by
 * report config with LinkTo, linksToSection, and groupBy support).
 */
export function generateReportColumns({
  reportData,
  isCustomQuery,
  configColumns,
  encryptedFields,
  groupByField,
  linksToSectionInfo,
  linkGensByCol,
  signedS3Urls,
}: GenerateReportColumnsParams): ColumnDef<any>[] {
  if (!reportData || reportData.columns.length === 0) return [];

  if (isCustomQuery) {
    return reportData.columns.map((col): ColumnDef<any> => ({
      id: col.key,
      accessorKey: col.key,
      header: col.label || formatColumnLabel(col.key),
      enableSorting: col.key !== 'uid' && col.key !== SYNTHETIC_VIEW_APPLICANTS_COLUMN_NAME,
      sortingFn: 'auto',
      sortDescFirst: false,
      cell: ({ getValue, row }: any) => {
        if (col.key === SYNTHETIC_VIEW_APPLICANTS_COLUMN_NAME) {
          return renderViewApplicantsCell(getValue() || row.original?.uid);
        }

        const value = getValue();
        if (value === null || value === undefined) return '--';

        if (col.key === 'uid') {
          return <CodeDisplay displayMode="inline" code={String(value)} copyable={true} className="text-xs" />;
        }

        const stringValue = String(value);

        if (isAidKitDocumentsUrl(stringValue)) {
          return (
            <div>
              {stringValue.split(',').map((url, index) => (
                <ZoomableImage key={`${col.key}_${index}`} url={signedS3Urls?.[url] ?? url} fullScreenSrc={url} />
              ))}
            </div>
          );
        }

        if (col.fieldType === 'Attachment') {
          return renderLinkCell(stringValue);
        }

        try {
          const url = new URL(stringValue);
          if (url.protocol === 'http:' || url.protocol === 'https:') {
            return renderLinkCell(stringValue, 'view');
          }
        } catch {
          // Not a URL, fall through
        }

        return stringValue;
      }
    }));
  }

  if (!configColumns) return [];

  const reportDataColumnsMap = new Map<string, ReportColumnData>();
  reportData.columns.forEach(col => {
    reportDataColumnsMap.set(col.key, col);
  });

  const orderedColumns: ReportColumnData[] = [];
  const sortableKeys = new Set<string>();
  configColumns.forEach((configCol: any) => {
    if (configCol.kind === 'Field') {
      if (configCol.field === SYNTHETIC_VIEW_APPLICANTS_COLUMN_NAME) {
        orderedColumns.push({ key: SYNTHETIC_VIEW_APPLICANTS_COLUMN_NAME, label: 'Actions' });
      } else {
        const lookupKey = configCol.field === SYNTHETIC_UID_COLUMN_NAME ? 'uid' : configCol.field;
        const columnData = reportDataColumnsMap.get(lookupKey);
        if (columnData) {
          orderedColumns.push({ ...columnData, encrypted: encryptedFields?.includes(configCol.field) });
          sortableKeys.add(lookupKey);
        }
      }
      return;
    }

    const colName = nameForColumn(configCol);
    if (!colName || orderedColumns.some(c => c.key === colName)) return;

    if (isOrderableColumn(configCol)) sortableKeys.add(colName);

    const linkTos = gatherLinkTos(configCol);
    const fromData = reportDataColumnsMap.get(colName);
    if (linkTos?.length) {
      const hideHeader = linkTos.some(lt => lt.hideColumnHeader);
      const label = linkTos[0]?.label?.en || colName;
      orderedColumns.push(fromData ?? { key: colName, label: hideHeader ? '' : label });
    } else if (fromData) {
      orderedColumns.push(fromData);
    }
  });

  const result: ColumnDef<any>[] = orderedColumns.map(col => {
    const colGens = linkGensByCol[col.key];
    const isLinkColumn = !!colGens?.length;
    const headerGen = colGens?.[0];
    return {
      id: col.key,
      accessorKey: col.key,
      header: headerGen?.hideHeader ? '' : col.encrypted ? () => (
        <span className="inline-flex items-center gap-2">
          <span>{col.label}</span>
          <Badge variant="warning">Sensitive</Badge>
        </span>
      ) : col.label,
      enableSorting: col.key !== 'uid' && col.key !== SYNTHETIC_VIEW_APPLICANTS_COLUMN_NAME && !isLinkColumn && sortableKeys.has(col.key),
      sortingFn: 'auto',
      sortDescFirst: false,
      cell: ({ getValue, row }: any) => {
        if (col.key === SYNTHETIC_VIEW_APPLICANTS_COLUMN_NAME) {
          return renderViewApplicantsCell(getValue() || row.original?.uid);
        }

        const value = getValue();

        if (isLinkColumn) {
          const stringValue = typeof value === 'string' ? value : '';
          const matched = stringValue
            ? colGens!.find((g) => g.templateRegex.test(stringValue))
            : undefined;
          if (matched) {
            return (
              <Button
                href={toAdminPanelHref(stringValue, matched.linkTo)}
                variant="link"
                openInNewWindow
                className="!justify-start"
              >
                {matched.label || stringValue}
              </Button>
            );
          }
        }

        if (value === null || value === undefined) return '--';

        if (col.key === 'uid') {
          return <CodeDisplay displayMode="inline" code={String(value)} copyable={true} className="text-xs" />;
        }

        if (col.fieldType === 'Attachment') {
          return renderLinkCell(String(value));
        }

        const stringValue = String(value);

        if (isAidKitDocumentsUrl(stringValue)) {
          return (
            <div>
              {stringValue.split(',').map((url, index) => (
                <ZoomableImage key={`${col.key}_${index}`} url={signedS3Urls?.[url] ?? url} fullScreenSrc={url} />
              ))}
            </div>
          );
        }

        try {
          const url = new URL(stringValue);
          if (url.protocol === 'http:' || url.protocol === 'https:') {
            return renderLinkCell(stringValue, 'view');
          }
        } catch {
          // Not a valid URL, fall through
        }

        return stringValue;
      }
    };
  });

  if (linksToSectionInfo) {
    result.push({
      id: SYNTHETIC_LINKS_TO_SECTION_COLUMN_NAME,
      accessorKey: SYNTHETIC_LINKS_TO_SECTION_COLUMN_NAME,
      header: linksToSectionInfo.label,
      enableSorting: false,
      cell: ({ row }: any) => {
        return renderLinksToSectionCell(row.original?.uid, linksToSectionInfo.subsurveyPath, linksToSectionInfo.label);
      }
    });
  }

  if (groupByField && !result.some(col => col.id === groupByField)) {
    result.push({
      id: groupByField,
      accessorKey: groupByField,
      header: groupByField,
      enableSorting: false,
    });
  }

  return result;
}
